import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Box } from '@material-ui/core';
import { Stack } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { selectOrganization, selectUser } from 'entities/user/selectors';
import { useRedirectIfNoCommunityAccess } from 'containers/CommunitiesPage/hasCommunityAccess';
import { EntityBanner } from 'components/TeamsCommunities/EntityBanner';
import { Features } from 'utils/features';
import { CommunityEntityType } from 'generated/graphql';

import { CommunitiesList } from './CommunitiesList';
import { TeamsList } from './TeamsList';
import { CommunitiesDisabledState } from './CommunitiesDisabledState';
import { TeamsDisabledState } from './TeamsDisabledState';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function getTabAccessibilityProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const CommunitiesPage = (): JSX.Element => {
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);
  const hasAccess = useRedirectIfNoCommunityAccess(user, organization);
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);

  // Set initial tab based on route
  useEffect(() => {
    if (location.pathname.startsWith('/teams')) {
      setTabValue(1);
    } else {
      setTabValue(0);
    }
  }, [location.pathname]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Helmet title="Groups" />
      <Stack spacing={4} style={{ marginBottom: 60 }}>
        <EntityBanner
          entityType={tabValue === 0 ? CommunityEntityType.Community : CommunityEntityType.Team}
          hasAccess={Boolean(hasAccess)}
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="teams and communities tabs">
            <Tab label="Communities" {...getTabAccessibilityProps(0)} />
            <Tab label="Teams" {...getTabAccessibilityProps(1)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {hasAccess && user?.hasFeature(Features.COMMUNITIES) ? (
            <CommunitiesList />
          ) : (
            <CommunitiesDisabledState />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {hasAccess && user?.hasFeature(Features.TEAMS) ? <TeamsList /> : <TeamsDisabledState />}
        </TabPanel>
      </Stack>
    </>
  );
};

export default CommunitiesPage;
